import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { showToast } from "@/app/_module/utils/show-toast";

import { type CreateLeadSchema } from "../schema/lead-schema";
import { leadsQueryKeys } from "../services/keys";
import { beneficiaryService, leadsService } from "../services/services";
import type { GetLeadsParams, Lead } from "../types/get-leads-type";
import type { GetLeadsStatResponseData } from "../types/search-lead-types";
import type {
  UploadLeadDocumentsRequest,
  UploadLeadDocumentsResponse,
} from "../types/lead-document-upload";
import type { DownloadInitiateResponse } from "../types/initiate-lead-document-dowload";
import type { LeadDocument } from "../types/lead-document-upload";
import type { CreateLeadNoteRequest } from "../types/create-lead-note";
import type { PipelineStage, CreatePipelineStageParams, UpdatePipelineStageParams } from "../types/pipeline-stage.types";
import type { GetLeadBeneficiariesResponse } from "../types/get-lead-beneficiaries";
import type { CreateLeadBeneficiariesSchema } from "../schema/beneficiary-schema";
import type { CreatePolicyParams } from "../types/create-policy-type";
import {
  Beneficiary,
  CreateBeneficiaryInput,
  UpdateBeneficiaryRequest,
} from "../types/Beneficiary";

// Hook to get leads with pagination and optional search
export const useGetLeads = (params: GetLeadsParams) => {
  return useQuery({
    queryKey: leadsQueryKeys.list(params),
    queryFn: () => leadsService.getLeads(params),
    // Keep previous data while fetching new data for smooth transitions
    placeholderData: (previousData) => previousData,
    staleTime: 30 * 1000, // 30 seconds - prevents immediate refetch on filter change
    refetchOnMount: true, // Always refetch when component mounts to get latest status changes
  });
};

// Hook to get lead statistics
export const useLeadStats = (range?: { startDate?: string; endDate?: string }) => {
  return useQuery<GetLeadsStatResponseData>({
    // Range is part of the key or switching the dashboard's date filter would serve the
    // previous window's cached numbers.
    queryKey: [...leadsQueryKeys.stats(), range?.startDate ?? 'all', range?.endDate ?? 'all'],
    queryFn: async () => {
      const result = await leadsService.getLeadStats(range);
      return result;
    },
    retry: (failureCount, error) => {
      if (failureCount >= 3) return false; // Allow more retries
      const errorMessage = error?.message || "";
      // Only skip retry for auth errors - be more permissive
      if (errorMessage.includes("401") || errorMessage.includes("403")) {
        return false;
      }
      // Retry everything else including 400s, 429s, 500s
      return true;
    },
    retryDelay: (attemptIndex) => Math.min(500 * (attemptIndex + 1), 2000), // Faster retries
    throwOnError: false,
    gcTime: 5 * 60 * 1000, // 5 minutes
    staleTime: 0, // Real-time: always refetch on mount for instant refresh
    refetchOnMount: true, // Ensure fresh data on mount
    refetchOnWindowFocus: true, // Refetch when returning to tab
  });
};

// Hook to get single lead
export const useLead = (leadId: string) => {
  return useQuery({
    queryKey: leadsQueryKeys.detail(leadId),
    queryFn: () => leadsService.getLead(leadId),
    enabled: !!leadId,
    refetchOnMount: true, // Ensure data is refetched when navigating to lead details
    retry: (failureCount, error) => {
      const msg = (error as any)?.message || (error as any)?.response?.status?.toString() || '';
      if (msg.includes('404') || msg.toLowerCase().includes('not found')) return false;
      return failureCount < 2;
    },
  });
};

// Hook to find lead by phone number
export const useLeadByPhone = (phone: string) => {
  return useQuery({
    queryKey: leadsQueryKeys.byPhone(phone),
    queryFn: async () => {
      if (!phone || phone.length !== 10) {
        return null;
      }
      // Search for leads with this phone number using 'query' parameter
      const result = await leadsService.getLeads({
        page: 1,
        limit: 20, // Get more results to find exact match
        query: phone
      });
      // Return the first matching lead if found
      if (result && "items" in result && Array.isArray(result.items) && result.items.length > 0) {
        // Verify the phone actually matches
        const lead = result.items.find((l: Lead) => {
          const leadPhone = l.phone?.replace(/\D/g, '');
          return leadPhone === phone || leadPhone?.endsWith(phone) || leadPhone?.includes(phone);
        });
        return lead || null;
      }
      return null;
    },
    enabled: !!phone && phone.length === 10,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook to create lead - now using CreateLeadSchema for better type safety
export const useCreateLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (leadData: CreateLeadSchema) =>
      leadsService.createLead(leadData),
    onMutate: async (leadData) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: leadsQueryKeys.lists() });
      await queryClient.cancelQueries({ queryKey: leadsQueryKeys.stats() });

      // Snapshot previous values
      const previousLeads = queryClient.getQueryData(leadsQueryKeys.lists());
      const previousStats = queryClient.getQueryData(leadsQueryKeys.stats());

      return { previousLeads, previousStats };
    },
    onSuccess: async () => {
      // Use synchronous cache invalidation to prevent race conditions
      try {
        await Promise.all([
          // Invalidate all lead list queries regardless of filters
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.all() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() }),
        ]);
      } catch (error) {}
    },
    onError: (error, variables, context) => {
      // Restore previous values on error
      if (context?.previousLeads) {
        queryClient.setQueryData(leadsQueryKeys.lists(), context.previousLeads);
      }
      if (context?.previousStats) {
        queryClient.setQueryData(leadsQueryKeys.stats(), context.previousStats);
      }

      // Handle specific backend errors that cause freezing
      const errorMessage =
        error && typeof error === "object" && "message" in error
          ? (error as { message: string }).message
          : "Unknown error";

      if (errorMessage.includes("500") || errorMessage.includes("timeout")) {
        // Force cache refresh to prevent UI freeze
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.all() });
        }, 500);
      }
    },
    // Add retry logic for temporary backend issues
    retry: (failureCount, error) => {
      if (failureCount < 2) {
        // Retry up to 2 times
        const errorMessage =
          error && typeof error === "object" && "message" in error
            ? (error as { message: string }).message
            : "";

        // Retry for 500 errors or timeouts
        if (
          errorMessage.includes("500") ||
          errorMessage.includes("timeout") ||
          errorMessage.includes("Network")
        ) {
          return true;
        }
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * (attemptIndex + 1), 3000), // Linear backoff
  });
};

// Hook to delete lead
// When deleteHouseholdMembers is true, also deletes all household members
export const useDeleteLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, deleteHouseholdMembers = false }: { leadId: string; deleteHouseholdMembers?: boolean }) =>
      leadsService.deleteLead(leadId, deleteHouseholdMembers),
    onMutate: async ({ leadId }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: leadsQueryKeys.lists() });
      await queryClient.cancelQueries({ queryKey: leadsQueryKeys.stats() });
      await queryClient.cancelQueries({ queryKey: leadsQueryKeys.deleted() });

      // Snapshot previous values
      const previousLeads = queryClient.getQueryData(leadsQueryKeys.lists());
      const previousStats = queryClient.getQueryData(leadsQueryKeys.stats());
      const previousDeleted = queryClient.getQueryData(
        leadsQueryKeys.deleted()
      );

      // Optimistically remove the lead from lists cache
      // Handle various response formats from the API
      // Use broader query key match to catch all leads queries
      queryClient.setQueriesData(
        { queryKey: ['leads'], exact: false },
        (old: any) => {
          if (!old) return old;
          // Handle { items: [...] } format
          if (old.items && Array.isArray(old.items)) {
            return {
              ...old,
              items: old.items.filter((lead: any) => lead.id !== leadId && lead.id?.toString() !== leadId),
            };
          }
          // Handle direct array format
          if (Array.isArray(old)) {
            return old.filter((lead: any) => lead.id !== leadId && lead.id?.toString() !== leadId);
          }
          // Handle { data: [...] } format
          if (old.data && Array.isArray(old.data)) {
            return {
              ...old,
              data: old.data.filter((lead: any) => lead.id !== leadId && lead.id?.toString() !== leadId),
            };
          }
          // Handle { data: { items: [...] } } format
          if (old.data?.items && Array.isArray(old.data.items)) {
            return {
              ...old,
              data: {
                ...old.data,
                items: old.data.items.filter((lead: any) => lead.id !== leadId && lead.id?.toString() !== leadId),
              },
            };
          }
          return old;
        }
      );

      return { previousLeads, previousStats, previousDeleted };
    },
    onSuccess: async () => {
      // Use synchronous cache invalidation to prevent race conditions
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.deleted() }),
        ]);
      } catch (error) {}
    },
    onError: (error, variables, context) => {
      // Restore previous values on error
      if (context?.previousLeads) {
        queryClient.setQueryData(leadsQueryKeys.lists(), context.previousLeads);
      }
      if (context?.previousStats) {
        queryClient.setQueryData(leadsQueryKeys.stats(), context.previousStats);
      }
      if (context?.previousDeleted) {
        queryClient.setQueryData(
          leadsQueryKeys.deleted(),
          context.previousDeleted
        );
      }

      // Handle specific backend errors that cause freezing
      const errorMessage =
        error && typeof error === "object" && "message" in error
          ? (error as { message: string }).message
          : "Unknown error";

      if (errorMessage.includes("500") || errorMessage.includes("timeout")) {
        // Force cache refresh to prevent UI freeze
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.all() });
        }, 500);
      }
    },
    // Add retry logic for temporary backend issues
    retry: (failureCount, error) => {
      if (failureCount < 2) {
        // Retry up to 2 times
        const errorMessage =
          error && typeof error === "object" && "message" in error
            ? (error as { message: string }).message
            : "";

        // Retry for 500 errors or timeouts
        if (
          errorMessage.includes("500") ||
          errorMessage.includes("timeout") ||
          errorMessage.includes("Network")
        ) {
          return true;
        }
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * (attemptIndex + 1), 3000), // Linear backoff
  });
};

// Hook to permanently delete lead
export const usePermanentlyDeleteLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (leadId: string) => leadsService.permanentlyDeleteLead(leadId),
    onMutate: async (leadId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: leadsQueryKeys.deleted() });

      // Snapshot previous values
      const previousDeleted = queryClient.getQueryData(
        leadsQueryKeys.deleted()
      );

      // Optimistically remove the lead from deleted cache
      queryClient.setQueriesData(
        { queryKey: leadsQueryKeys.deleted() },
        (old: any) => {
          if (!old || !old.items) return old;
          return {
            ...old,
            items: old.items.filter((lead: any) => lead.id !== leadId),
          };
        }
      );

      return { previousDeleted };
    },
    onSuccess: async () => {
      // Use synchronous cache invalidation to prevent race conditions
      try {
        await queryClient.invalidateQueries({
          queryKey: leadsQueryKeys.deleted(),
        });
      } catch (error) {}
    },
    onError: (error, leadId, context) => {
      // Restore previous values on error
      if (context?.previousDeleted) {
        queryClient.setQueryData(
          leadsQueryKeys.deleted(),
          context.previousDeleted
        );
      }
    },
  });
};

// Hook to permanently delete all deleted leads
export const usePermanentlyDeleteAllLeads = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => leadsService.permanentlyDeleteAllLeads(),
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: leadsQueryKeys.deleted() });

      // Snapshot previous values
      const previousDeleted = queryClient.getQueryData(
        leadsQueryKeys.deleted()
      );

      // Optimistically clear all deleted leads from cache
      queryClient.setQueriesData(
        { queryKey: leadsQueryKeys.deleted() },
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            items: [],
          };
        }
      );

      return { previousDeleted };
    },
    onSuccess: async () => {
      // Use synchronous cache invalidation to prevent race conditions
      try {
        await queryClient.invalidateQueries({
          queryKey: leadsQueryKeys.deleted(),
        });
      } catch (error) {}
    },
    onError: (error, variables, context) => {
      // Restore previous values on error
      if (context?.previousDeleted) {
        queryClient.setQueryData(
          leadsQueryKeys.deleted(),
          context.previousDeleted
        );
      }
    },
  });
};

// Hook for fetching available statuses
export const useAvailableStatuses = () => {
  return useQuery({
    queryKey: leadsQueryKeys.statusesAvailable(),
    queryFn: async () => {
      const data = await leadsService.getStatusesAvailable();

      if (!data) {
        throw new Error("No data returned from available statuses API");
      }

      return data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes — pipeline stage changes need to reflect quickly
    gcTime: 10 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    throwOnError: false,
    refetchOnMount: true,
  });
};

// Hook for fetching simple statuses
export const useSimpleStatuses = () => {
  return useQuery({
    queryKey: leadsQueryKeys.statusesSimple(),
    queryFn: () => leadsService.getStatusesSimple(),
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: true,
  });
};

// Hook for fetching all leads for overview calculations
export const useLeadsForOverview = () => {
  // lite: the only consumer (BulkEmailFollowUpModal) reads id / email / firstName /
  // lastName — all scalar columns. Same reason as the /communication caller.
  const params: GetLeadsParams = { page: 1, limit: 500, lite: 'true' };

  return useQuery<Lead[]>({
    queryKey: leadsQueryKeys.list(params),
    queryFn: async (): Promise<Lead[]> => {
      const response = await leadsService.getLeads(params);
      if (response && "items" in response && Array.isArray(response.items)) {
        return response.items;
      }

      return [];
    },
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: true,
  });
};

// Hook to update lead
export const useUpdateLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      data,
    }: {
      leadId: string;
      data: Parameters<typeof leadsService.updateLead>[1];
    }) => leadsService.updateLead(leadId, data),
    onMutate: async ({ leadId, data }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await Promise.all([
        queryClient.cancelQueries({ queryKey: leadsQueryKeys.lists() }),
        queryClient.cancelQueries({ queryKey: leadsQueryKeys.detail(leadId) }),
        queryClient.cancelQueries({ queryKey: leadsQueryKeys.stats() }),
        queryClient.cancelQueries({ queryKey: leadsQueryKeys.overview() }),
      ]);

      // Snapshot previous values
      const previousLeads = queryClient.getQueryData(leadsQueryKeys.lists());
      const previousLead = queryClient.getQueryData(
        leadsQueryKeys.detail(leadId)
      );
      const previousStats = queryClient.getQueryData(leadsQueryKeys.stats());
      const previousOverview = queryClient.getQueryData(
        leadsQueryKeys.overview()
      );

      // Optimistically update the lead in cache
      if (data.leadStat) {
        queryClient.setQueryData(leadsQueryKeys.detail(leadId), (old: any) => ({
          ...old,
          leadStat: data.leadStat,
        }));

        // Update lead in lists cache
        queryClient.setQueriesData(
          { queryKey: leadsQueryKeys.lists() },
          (old: any) => {
            if (!old || !old.items) return old;
            return {
              ...old,
              items: old.items.map((lead: any) =>
                lead.id === leadId ? { ...lead, leadStat: data.leadStat } : lead
              ),
            };
          }
        );
      }

      return { previousLeads, previousLead, previousStats, previousOverview };
    },
    onSuccess: async (updatedLead, { leadId, data }) => {
      // Use synchronous cache invalidation to prevent race conditions
      try {
        // Check if this is a beneficiary lead by looking at cached data
        // Beneficiaries have a leadId field pointing to their parent
        let parentLeadId: string | undefined;

        // The response structure from handleApiCall can be:
        // 1. { lead: {...} } - wrapped format
        // 2. {...lead data...} - direct lead object
        let updatedLeadData: any = null;

        if (updatedLead && typeof updatedLead === 'object') {
          if ('lead' in updatedLead && updatedLead.lead) {
            // Wrapped format: { lead: {...} }
            updatedLeadData = updatedLead.lead;
            parentLeadId = updatedLead.lead.leadId;
          } else if ('id' in updatedLead) {
            // Direct format: the response IS the lead object
            updatedLeadData = updatedLead;
            parentLeadId = (updatedLead as any).leadId;
          }
        }

        // If not in response, check the cache for the lead details
        if (!parentLeadId) {
          const cachedLead = queryClient.getQueryData(
            leadsQueryKeys.detail(leadId)
          ) as any;
          parentLeadId = cachedLead?.leadId;
        }

        // If still not found, check in the lists cache
        if (!parentLeadId) {
          const listsCache = queryClient.getQueriesData({
            queryKey: leadsQueryKeys.lists(),
          });
          for (const [, cachedData] of listsCache) {
            const items = (cachedData as any)?.items;
            if (Array.isArray(items)) {
              const lead = items.find((l: any) => l.id === leadId);
              if (lead?.leadId) {
                parentLeadId = lead.leadId;
                break;
              }
            }
          }
        }

        // Update the cache with the new data if available
        if (updatedLeadData) {
          queryClient.setQueryData(
            leadsQueryKeys.detail(leadId),
            updatedLeadData
          );

          // Update the lead in all lists using the RESPONSE data (not the request payload)
          // Use a partial key match ['leads', 'list'] to catch all list queries regardless of filters
          queryClient.setQueriesData(
            { queryKey: ['leads', 'list'] },
            (old: any) => {
              if (!old || !old.items) return old;
              return {
                ...old,
                items: old.items.map((lead: any) =>
                  lead.id === leadId ? { ...lead, ...updatedLeadData } : lead
                ),
              };
            }
          );
        }

        // Build list of queries to invalidate
        const queriesToInvalidate = [
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() }),
          queryClient.invalidateQueries({
            queryKey: leadsQueryKeys.detail(leadId),
          }),
          queryClient.invalidateQueries({
            queryKey: leadsQueryKeys.overview(),
          }),
          queryClient.invalidateQueries({ queryKey: ["lead-audits", leadId] }), // Refresh activity timeline
        ];

        // If this is a beneficiary, also invalidate the parent lead's detail query
        // so the beneficiary list refreshes on the parent lead's detail page
        if (parentLeadId) {
          queriesToInvalidate.push(
            queryClient.invalidateQueries({
              queryKey: leadsQueryKeys.detail(parentLeadId),
            }),
            queryClient.invalidateQueries({
              queryKey: leadsQueryKeys.beneficiaries(parentLeadId),
            })
          );
        }

        // Then invalidate all related queries to ensure fresh data
        await Promise.all(queriesToInvalidate);

        // Force refetch of leads list to ensure UI updates
        await queryClient.refetchQueries({ queryKey: leadsQueryKeys.lists() });
      } catch (error) {
        // Fallback: try to invalidate all leads queries instead of page refresh
        try {
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.all() });
        } catch (fallbackError) {}
      }
    },
    onError: (error, { leadId }, context) => {
      // Extract error message for proper handling
      const errorMessage =
        error && typeof error === "object" && "message" in error
          ? (error as { message: string }).message
          : "Unknown error";

      // Check if this is a 404 error (lead not found)
      const is404Error =
        errorMessage.includes("404") ||
        errorMessage.toLowerCase().includes("not found") ||
        errorMessage.toLowerCase().includes("lead not found");

      if (is404Error) {
        // For 404 errors, invalidate the cache immediately to remove stale leads
        // Don't restore previous values - the lead doesn't exist anymore
        queryClient.invalidateQueries({ queryKey: leadsQueryKeys.lists() });
        queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() });
        queryClient.invalidateQueries({
          queryKey: leadsQueryKeys.detail(leadId),
        });
        queryClient.invalidateQueries({ queryKey: leadsQueryKeys.overview() });
        return; // Exit early - don't restore cache for 404s
      }

      // For non-404 errors, restore previous values
      if (context?.previousLeads) {
        queryClient.setQueryData(leadsQueryKeys.lists(), context.previousLeads);
      }
      if (context?.previousLead) {
        queryClient.setQueryData(
          leadsQueryKeys.detail(leadId),
          context.previousLead
        );
      }
      if (context?.previousStats) {
        queryClient.setQueryData(leadsQueryKeys.stats(), context.previousStats);
      }
      if (context?.previousOverview) {
        queryClient.setQueryData(
          leadsQueryKeys.overview(),
          context.previousOverview
        );
      }

      // Handle specific backend errors that cause freezing
      if (errorMessage.includes("500") || errorMessage.includes("timeout")) {
        // Force cache refresh to prevent UI freeze
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.all() });
        }, 500);
      }
    },
    // Add retry logic for temporary backend issues
    retry: (failureCount, error) => {
      if (failureCount < 2) {
        // Retry up to 2 times
        const errorMessage =
          error && typeof error === "object" && "message" in error
            ? (error as { message: string }).message
            : "";

        // Retry for 500 errors or timeouts
        if (
          errorMessage.includes("500") ||
          errorMessage.includes("timeout") ||
          errorMessage.includes("Network")
        ) {
          return true;
        }
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * (attemptIndex + 1), 3000), // Linear backoff
  });
};

// Hook to get lead with policies
export const useLeadWithPolicies = (leadId: string) => {
  return useQuery({
    queryKey: leadsQueryKeys.detailWithPolicies(leadId),
    queryFn: () => leadsService.getLeadWithPolicies(leadId),
    enabled: !!leadId,
    refetchOnMount: true, // Ensure data is refetched when navigating to lead details
    retry: (failureCount, error) => {
      const msg = (error as any)?.message || (error as any)?.response?.status?.toString() || '';
      if (msg.includes('404') || msg.toLowerCase().includes('not found')) return false;
      return failureCount < 2;
    },
  });
};

// Hook to get deleted leads. Optional `q` does server-side fuzzy search
// across name / phone / email — phone is normalized to digits-only on both
// sides so "(215) 386" and "2153862906" both match.
export const useDeletedLeads = (
  q?: string,
  page: number = 1,
  limit: number = 50,
) => {
  return useQuery({
    queryKey: leadsQueryKeys.deleted(q, page, limit),
    queryFn: () => leadsService.getDeletedLeads(q, page, limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: true,
  });
};

// Hook to restore deleted lead
export const useRestoreDeletedLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (leadId: string) => leadsService.restoreDeletedLead(leadId),
    onSuccess: async () => {
      // Use synchronous cache invalidation to prevent race conditions
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.deleted() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() }),
        ]);
      } catch (error) {}
    },
  });
};

// Hook to restore all deleted leads
export const useRestoreAllDeletedLeads = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => leadsService.restoreAllDeletedLeads(),
    onSuccess: async () => {
      // Use synchronous cache invalidation to prevent race conditions
      try {
        await Promise.all([
          // Invalidate deleted leads cache
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.deleted() }),
          // Invalidate ALL leads queries (includes all list variations with different filters)
          queryClient.invalidateQueries({ queryKey: ['leads'], exact: false }),
          // Specifically invalidate stats
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() }),
        ]);
        // Force refetch of all leads queries to ensure data is fresh
        await queryClient.refetchQueries({ queryKey: ['leads'], exact: false });
      } catch (error) {}
    },
  });
};

// ===== NEW DOCUMENT MANAGEMENT HOOKS =====

// Upload lead documents
export const useUploadLeadDocuments = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UploadLeadDocumentsResponse,
    Error,
    UploadLeadDocumentsRequest
  >({
    mutationFn: async ({ leadId, files }) => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });

      const result = await handleApiCall<UploadLeadDocumentsResponse>(
        async () => {
          const response = await api.post({
            url: getApiUrl(endpoint.leads.uploadLeadDocuments(leadId)),
            body: formData,
            auth: true, // Add authentication
            options: {
              headers: {
                // Don't set Content-Type for FormData - let browser set it with boundary
              },
            },
          });
          return response;
        }
      );

      return result;
    },
    onSuccess: async (data, variables) => {
      // FIX: Invalidate documents query to refresh the list after upload
      await queryClient.invalidateQueries({
        queryKey: leadsQueryKeys.documents(variables.leadId),
      });
    },
    onError: (error) => {
      console.error("Document upload failed:", error);
    },
  });
};

// Get lead documents
export const useLeadDocuments = (leadId: string) => {
  return useQuery<LeadDocument[]>({
    queryKey: leadsQueryKeys.documents(leadId),
    queryFn: async () => {
      const result = await handleApiCall<any>(async () => {
        const response = await api.get({
          url: getApiUrl(endpoint.leads.getLeadDocuments(leadId)),
          auth: true, // Add authentication
        });
        return response;
      });
      // handleApiCall already extracts response.data, so result is { documents: [...], count: N }
      if (result?.documents) {
        return result.documents;
      }
      // Fallback if response is already an array
      if (Array.isArray(result)) {
        return result;
      }
      return [];
    },
    enabled: !!leadId,
    refetchOnMount: "always",
    staleTime: 0,
  });
};

// Delete lead document
export const useDeleteLeadDocument = (leadId: string) => {
  return useMutation<void, Error, string>({
    mutationFn: async (documentId: string) => {
      await handleApiCall(async () => {
        const response = await api.delete({
          url: getApiUrl(endpoint.leads.deleteLeadDocument(leadId, documentId)),
          auth: true, // Add authentication
        });
        return response;
      });
    },
    // Enhanced retry logic
    retry: (failureCount, error) => {
      if (failureCount < 3) {
        const errorMessage =
          error && typeof error === "object" && "message" in error
            ? (error as { message: string }).message
            : "";

        if (
          errorMessage.includes("500") ||
          errorMessage.includes("502") ||
          errorMessage.includes("503") ||
          errorMessage.includes("timeout") ||
          errorMessage.includes("Network")
        ) {
          return true;
        }
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(400 * (attemptIndex + 1), 2000),
  });
};

// Initiate lead document download
export const useInitiateLeadDocumentDownload = (leadId: string) => {
  return useMutation<DownloadInitiateResponse, Error, string>({
    mutationFn: async (documentId: string) => {
      const result = await handleApiCall<DownloadInitiateResponse>(async () => {
        const response = await api.post<DownloadInitiateResponse>({
          url: getApiUrl(
            endpoint.leads.initiateLeadDocumentDownload(leadId, documentId)
          ),
          auth: true,
        });
        return response;
      });
      return result;
    },
  });
};

// Verify lead document download with OTP
export const useVerifyLeadDocumentDownload = (leadId: string) => {
  return useMutation<
    { downloadToken: string },
    Error,
    {
      documentId: string;
      verificationCode: string;
      method?: "sms" | "totp" | "backup";
    }
  >({
    mutationFn: async ({ documentId, verificationCode, method }) => {
      const result = await handleApiCall<{ downloadToken: string }>(
        async () => {
          // Only include method in data if it's defined (validator requires exact values or undefined)
          const requestData: { verificationCode: string; method?: string } = { verificationCode };
          if (method) {
            requestData.method = method;
          }

          const response = await api.post<{ downloadToken: string }>({
            url: getApiUrl(
              endpoint.leads.verifyLeadDocumentDownload(leadId, documentId)
            ),
            auth: true,
            body: requestData,
          });
          return response;
        }
      );
      return result;
    },
  });
};

// Get document download URL with token
export const useGetDocumentDownloadUrl = () => {
  return useMutation<{ url: string; expiresAt: string }, Error, string>({
    mutationFn: async (downloadToken: string) => {
      const result = await handleApiCall<{ url: string; expiresAt: string }>(
        async () => {
          const response = await api.get<{ url: string; expiresAt: string }>({
            url:
              getApiUrl(endpoint.leads.getDocumentDownloadUrl()) +
              `?token=${downloadToken}`,
            auth: true,
          });
          return response;
        }
      );
      return result;
    },
  });
};

// Lead notes hooks
export const useGetLeadNotes = (leadId: string) => {
  return useQuery({
    queryKey: leadsQueryKeys.notes(leadId), // Use notes-specific key
    queryFn: async () => {
      // Use the correct endpoint for getting notes (GET /leads/:leadId/notes)
      const url = getApiUrl(endpoint.leads.getLeadNotes(leadId));

      const result = await handleApiCall(async () => {
        const response = await api.get({
          url,
          auth: true, // Add authentication
        });
        return response;
      });

      return result;
    },
    enabled: !!leadId,
    staleTime: 1 * 60 * 1000, // 1 minute - notes can change frequently
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(300 * (attemptIndex + 1), 1500),
    refetchOnWindowFocus: true,
    // CRITICAL: Prevent infinite re-renders by not refetching on every mount
    refetchOnMount: false,
    // Enable structural sharing to prevent new object references for same data
    structuralSharing: true,
  });
};

export const useCreateLeadNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      params,
    }: {
      leadId: string;
      params: CreateLeadNoteRequest;
    }) => {
      const url = getApiUrl(endpoint.leads.createLeadNote(leadId));

      const result = await handleApiCall(async () => {
        const response = await api.post({
          url,
          body: params,
          auth: true, // Add authentication
        });
        return response;
      });

      return result;
    },
    onSuccess: (_, variables) => {
      // Invalidate lead details, notes, audits, and activity queries to refresh UI
      queryClient.invalidateQueries({
        queryKey: leadsQueryKeys.detail(variables.leadId),
      });
      queryClient.invalidateQueries({
        queryKey: leadsQueryKeys.notes(variables.leadId),
      });
      queryClient.invalidateQueries({
        queryKey: ["lead-audits", variables.leadId],
      });
      queryClient.invalidateQueries({
        queryKey: ["lead-activity", variables.leadId],
      });
    },
    onError: (error) => {
      console.error("Failed to create note:", error);
    },
    // Enhanced retry logic
    retry: (failureCount, error) => {
      if (failureCount < 3) {
        const errorMessage =
          error && typeof error === "object" && "message" in error
            ? (error as { message: string }).message
            : "";

        if (
          errorMessage.includes("500") ||
          errorMessage.includes("502") ||
          errorMessage.includes("503") ||
          errorMessage.includes("timeout") ||
          errorMessage.includes("Network")
        ) {
          return true;
        }
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(500 * (attemptIndex + 1), 2000),
  });
};

export const useDeleteLeadNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      noteId,
    }: {
      leadId: string;
      noteId: string;
    }) => {
      await handleApiCall(async () => {
        const response = await api.delete({
          url: getApiUrl(endpoint.leads.deleteLeadNote(leadId, noteId)),
          auth: true, // Add authentication
        });
        return response;
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate lead details, notes, audits, and activity queries to refresh UI
      queryClient.invalidateQueries({
        queryKey: leadsQueryKeys.detail(variables.leadId),
      });
      queryClient.invalidateQueries({
        queryKey: leadsQueryKeys.notes(variables.leadId),
      });
      queryClient.invalidateQueries({
        queryKey: ["lead-audits", variables.leadId],
      });
      queryClient.invalidateQueries({
        queryKey: ["lead-activity", variables.leadId],
      });
    },
    onError: (error) => {
      console.error("Failed to delete note:", error);
    },
    // Enhanced retry logic
    retry: (failureCount, error) => {
      if (failureCount < 3) {
        const errorMessage =
          error && typeof error === "object" && "message" in error
            ? (error as { message: string }).message
            : "";

        if (
          errorMessage.includes("500") ||
          errorMessage.includes("502") ||
          errorMessage.includes("503") ||
          errorMessage.includes("timeout") ||
          errorMessage.includes("Network")
        ) {
          return true;
        }
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(500 * (attemptIndex + 1), 2000),
  });
};

export const useUpdateLeadNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      noteId,
      params,
    }: {
      leadId: string;
      noteId: string;
      params: { note?: string; category?: string; priority?: string };
    }) => {
      const url = getApiUrl(endpoint.leads.updateLeadNote(leadId, noteId));

      const result = await handleApiCall(async () => {
        const response = await api.patch({
          url,
          body: params,
          auth: true,
        });
        return response;
      });

      return result;
    },
    onSuccess: (_, variables) => {
      // Invalidate both lead details and notes queries to refresh UI
      queryClient.invalidateQueries({
        queryKey: leadsQueryKeys.detail(variables.leadId),
      });
      queryClient.invalidateQueries({
        queryKey: leadsQueryKeys.notes(variables.leadId),
      });
    },
    onError: (error) => {
      console.error("Failed to update note:", error);
    },
    // Enhanced retry logic
    retry: (failureCount, error) => {
      if (failureCount < 3) {
        const errorMessage =
          error && typeof error === "object" && "message" in error
            ? (error as { message: string }).message
            : "";

        if (
          errorMessage.includes("500") ||
          errorMessage.includes("502") ||
          errorMessage.includes("503") ||
          errorMessage.includes("timeout") ||
          errorMessage.includes("Network")
        ) {
          return true;
        }
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(500 * (attemptIndex + 1), 2000),
  });
};

export const useRevealLeadNote = () => {
  return useMutation({
    mutationFn: async ({
      leadId,
      noteId,
    }: {
      leadId: string;
      noteId: string;
    }) => {
      const url = getApiUrl(endpoint.leads.revealLeadNote(leadId, noteId));
      const result = await handleApiCall(async () => {
        const response = await api.get({
          url,
          auth: true,
        });
        return response;
      });
      return result as { note: string };
    },
  });
};

// Lead beneficiaries hooks
export const useCreateLeadBeneficiaries = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateLeadBeneficiariesSchema) => {
      const result = await handleApiCall(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.leads.createLeadBeneficiaries),
          body: data,
          auth: true, // Add authentication
        });
        return response;
      });
      return result;
    },
    onSuccess: (data, variables) => {
      showToast("Beneficiaries created successfully", "success");
      // Invalidate both lead details and beneficiaries queries to refresh UI
      queryClient.invalidateQueries({
        queryKey: leadsQueryKeys.detail(variables.parentLeadId),
      });
      queryClient.invalidateQueries({
        queryKey: leadsQueryKeys.beneficiaries(variables.parentLeadId),
      });
      // Also invalidate leads list to update isBeneficiary flag in the table
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.lists() });
      // Refresh activity timeline for parent lead
      queryClient.invalidateQueries({
        queryKey: ["lead-audits", variables.parentLeadId],
      });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to create beneficiaries';
      showToast(message, 'error');
    },
  });
};

export const useGetLeadBeneficiaries = (
  leadId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: leadsQueryKeys.beneficiaries(leadId),
    queryFn: async () => {
      const url = getApiUrl(endpoint.leads.getLeadBeneficiaries(leadId));

      const result = await handleApiCall<GetLeadBeneficiariesResponse>(
        async () => {
          const response = await api.get({
            url,
            auth: true, // Add authentication
          });
          return response;
        }
      );

      return result;
    },
    enabled: !!leadId && enabled, // Only fetch when leadId exists AND enabled is true
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
  });
};

export const useUpdateLeadBeneficiary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      beneficiaryId,
      data,
    }: {
      leadId: string;
      beneficiaryId: string;
      data: any;
    }) => {
      const result = await handleApiCall(async () => {
        const response = await api.patch({
          url: getApiUrl(endpoint.leads.updateLeadBeneficiary(leadId, beneficiaryId)),
          body: data,
          auth: true, // Add authentication
        });
        return response;
      });
      return result;
    },
    onSuccess: (_, variables) => {
      // Invalidate both lead details and beneficiaries queries to refresh UI
      queryClient.invalidateQueries({
        queryKey: leadsQueryKeys.detail(variables.leadId),
      });
      queryClient.invalidateQueries({
        queryKey: ["beneficiaries", variables.leadId],
      });
    },
  });
};

// export const useDeleteLeadBeneficiary = () => {
//     const queryClient = useQueryClient();

//     return useMutation({
//         mutationFn: async ({ beneficiaryId, leadId }: { beneficiaryId: string; leadId: string }) => {
//             const result = await handleApiCall(async () => {
//                 const response = await api.delete({
//                     url: getApiUrl(endpoint.leads.deleteLeadBeneficiary(leadId, beneficiaryId)),
//                     auth: true, // Add authentication
//                 });
//                 return response;
//             });
//             return result;
//         },
//         onSuccess: (_, variables) => {
//             // Invalidate both lead details and beneficiaries queries to refresh UI
//             queryClient.invalidateQueries({ queryKey: leadsQueryKeys.detail(variables.leadId) });
//             queryClient.invalidateQueries({ queryKey: leadsQueryKeys.beneficiaries(variables.leadId) });
//         },
//     });
// };

// Lead policy hooks
export const useUpdatePolicy = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ policyId, data }: { policyId: string; data: any }) => {
      const result = await handleApiCall(async () => {
        const response = await api.put({
          url: getApiUrl(endpoint.policy.updatePolicy(policyId)),
          body: data,
          auth: true,
        });
        return response;
      });
      return result;
    },
    onSuccess: async (updatedPolicy, variables) => {
      const { leadId } = variables.data;

      // Extract policy data from response (handle both wrapped and direct formats)
      let policyData: any = null;
      if (updatedPolicy && typeof updatedPolicy === 'object') {
        if ('policy' in updatedPolicy && updatedPolicy.policy) {
          policyData = updatedPolicy.policy;
        } else if ('id' in updatedPolicy) {
          policyData = updatedPolicy;
        }
      }

      // Determine the new lead status based on policy stage
      const policyStage = policyData?.policyStage || variables.data.policyStage;
      let newLeadStat: string | null = null;
      if (policyStage === 'sold') {
        newLeadStat = 'Sold & Approved';
      } else if (policyStage === 'underwriting') {
        newLeadStat = 'Underwriting';
      }

      // Update the cache immediately with the response data
      if (policyData && leadId) {
        // Update lead detail with policies cache
        queryClient.setQueryData(
          leadsQueryKeys.detailWithPolicies(leadId),
          (old: any) => {
            if (!old) return old;
            const policies = old.policies || [];
            return {
              ...old,
              leadStat: newLeadStat || old.leadStat,
              policies: policies.map((p: any) =>
                p.id === variables.policyId ? { ...p, ...policyData } : p
              ),
            };
          }
        );

        // Update lead detail cache (if it has policies embedded)
        queryClient.setQueryData(
          leadsQueryKeys.detail(leadId),
          (old: any) => {
            if (!old || !old.policies) return old;
            return {
              ...old,
              leadStat: newLeadStat || old.leadStat,
              policies: old.policies.map((p: any) =>
                p.id === variables.policyId ? { ...p, ...policyData } : p
              ),
            };
          }
        );

        // Update the lead's status in the leads list cache
        if (newLeadStat) {
          queryClient.setQueriesData(
            { queryKey: ['leads', 'list'] },
            (old: any) => {
              if (!old || !old.items) return old;
              return {
                ...old,
                items: old.items.map((lead: any) =>
                  lead.id === leadId ? { ...lead, leadStat: newLeadStat } : lead
                ),
              };
            }
          );
        }
      }

      // Then invalidate and refetch to ensure fresh data
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: leadsQueryKeys.detail(leadId) }),
        queryClient.invalidateQueries({ queryKey: leadsQueryKeys.detailWithPolicies(leadId) }),
        queryClient.invalidateQueries({ queryKey: ['leads', 'list'] }),
        queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() }),
      ]);

      // Force refetch leads list to ensure UI is updated before navigation
      await queryClient.refetchQueries({ queryKey: ['leads', 'list'] });
    },
  });
};

// Delete policy
export const useDeletePolicy = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ policyId, leadId }: { policyId: string; leadId: string }) => {
      const result = await handleApiCall(async () => {
        const response = await api.delete({
          url: getApiUrl(endpoint.policy.deletePolicy(policyId)),
          auth: true,
        });
        return response;
      });
      return result;
    },
    onSuccess: async (_, variables) => {
      const { policyId, leadId } = variables;

      // Optimistically remove the policy from cache immediately
      queryClient.setQueryData(
        leadsQueryKeys.detailWithPolicies(leadId),
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            policies: (old.policies || []).filter((p: any) => p.id !== policyId),
          };
        }
      );

      // Also update lead detail cache (if it has policies embedded)
      queryClient.setQueryData(
        leadsQueryKeys.detail(leadId),
        (old: any) => {
          if (!old || !old.policies) return old;
          return {
            ...old,
            policies: old.policies.filter((p: any) => p.id !== policyId),
          };
        }
      );

      // Then invalidate and refetch to ensure fresh data
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: leadsQueryKeys.detail(leadId) }),
        queryClient.invalidateQueries({ queryKey: leadsQueryKeys.detailWithPolicies(leadId) }),
        queryClient.invalidateQueries({ queryKey: ['leads', 'list'] }),
        queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() }),
      ]);

      // Force refetch leads list to ensure UI is updated before navigation
      await queryClient.refetchQueries({ queryKey: ['leads', 'list'] });
    },
  });
};

// Create policy for a lead
export const useCreatePolicy = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (policyData: CreatePolicyParams) => {
      return await leadsService.createPolicy(policyData);
    },
    onSuccess: async (createdPolicy, variables) => {
      const { leadId } = variables;

      // Extract policy data from response (handle both wrapped and direct formats)
      let policyData: any = null;
      if (createdPolicy && typeof createdPolicy === 'object') {
        if ('policy' in createdPolicy && createdPolicy.policy) {
          policyData = createdPolicy.policy;
        } else if ('data' in createdPolicy && (createdPolicy as any).data?.data) {
          // Handle nested response: { data: { data: PolicyData } }
          policyData = (createdPolicy as any).data.data;
        } else if ('id' in createdPolicy) {
          policyData = createdPolicy;
        }
      }

      // Determine the new lead status based on policy stage
      // policyStage: 'underwriting' -> leadStat: 'Underwriting'
      // policyStage: 'sold' -> leadStat: 'Sold & Approved'
      const policyStage = policyData?.policyStage || variables.policyStage;
      let newLeadStat: string | null = null;
      if (policyStage === 'sold') {
        newLeadStat = 'Sold & Approved';
      } else if (policyStage === 'underwriting') {
        newLeadStat = 'Underwriting';
      }

      // Add the new policy to cache immediately
      if (policyData && leadId) {
        // Update lead detail with policies cache
        queryClient.setQueryData(
          leadsQueryKeys.detailWithPolicies(leadId),
          (old: any) => {
            if (!old) return old;
            return {
              ...old,
              leadStat: newLeadStat || old.leadStat,
              policies: [...(old.policies || []), policyData],
            };
          }
        );

        // Update lead detail cache (if it has policies embedded)
        queryClient.setQueryData(
          leadsQueryKeys.detail(leadId),
          (old: any) => {
            if (!old) return old;
            return {
              ...old,
              leadStat: newLeadStat || old.leadStat,
              policies: [...(old.policies || []), policyData],
            };
          }
        );

        // Update the lead's status in the leads list cache
        if (newLeadStat) {
          queryClient.setQueriesData(
            { queryKey: ['leads', 'list'] },
            (old: any) => {
              if (!old || !old.items) return old;
              return {
                ...old,
                items: old.items.map((lead: any) =>
                  lead.id === leadId ? { ...lead, leadStat: newLeadStat } : lead
                ),
              };
            }
          );
        }
      }

      // Then invalidate and refetch to ensure fresh data
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: leadsQueryKeys.detail(leadId),
        }),
        queryClient.invalidateQueries({
          queryKey: leadsQueryKeys.detailWithPolicies(leadId),
        }),
        queryClient.invalidateQueries({ queryKey: ['leads', 'list'] }),
        queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() }),
      ]);

      // Force refetch leads list to ensure UI is updated before navigation
      await queryClient.refetchQueries({ queryKey: ['leads', 'list'] });
    },
  });
};

// Hook to get agency carriers
export const useGetAgencyCarriers = (agencyId?: string) => {
  return useQuery({
    queryKey: ["agency-carriers", agencyId],
    queryFn: async () => {
      // If agencyId is provided, filter by agency
      const url = agencyId
        ? getApiUrl(`/agency-carriers?agencyId=${agencyId}`)
        : getApiUrl("/agency-carriers");

      const result = await handleApiCall(async () => {
        const response = await api.get({
          url,
          auth: true,
        });
        return response;
      });

      let carriers = Array.isArray(result) ? result : [];

      // Filter by agency if agencyId is provided
      if (agencyId) {
        carriers = carriers.filter(
          (carrier: any) => carrier.agencyId === agencyId || !carrier.agencyId
        );
      }

      // Return only enabled carriers
      return carriers.filter((carrier: any) => carrier.isEnabled);
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

// Hook to get agency agents/users
// Note: This requires the agencyId to be passed from the component using useUserContext
export const useGetAgencyAgents = (agencyId?: string) => {
  return useQuery({
    queryKey: ["agency-agents", agencyId],
    queryFn: async () => {
      if (!agencyId) {
        return [];
      }
      const result = await handleApiCall(async () => {
        const response = await api.get({
          url: getApiUrl(`/agencies/${agencyId}/users`),
          auth: true,
        });
        return response;
      });
      // Backend returns { items: [...] }
      return result?.items || [];
    },
    enabled: !!agencyId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

// Hook to get lead audit history
export const useGetLeadAudits = (leadId: string) => {
  return useQuery({
    queryKey: ["lead-audits", leadId],
    queryFn: async () => {
      if (!leadId) {
        return [];
      }
      const result = await handleApiCall(async () => {
        const response = await api.get({
          url: getApiUrl(endpoint.leads.getLeadAudits(leadId)),
          auth: true,
        });
        return response;
      });
      return result || [];
    },
    enabled: !!leadId,
    staleTime: 0, // Always fetch fresh audit data
    refetchOnMount: "always",
  });
};

// Hook to get lead communication activity (calls, emails, SMS, notes)
export const useGetLeadActivity = (leadId: string) => {
  return useQuery({
    queryKey: ["lead-activity", leadId],
    queryFn: async () => {
      if (!leadId) {
        return [];
      }
      const result = await handleApiCall(async () => {
        const response = await api.get({
          url: getApiUrl(`/api/leads/${leadId}/activity`),
          auth: true,
        });
        return response;
      });
      return result || [];
    },
    enabled: !!leadId,
    staleTime: 0, // Always fetch fresh activity data
    refetchOnMount: "always",
    // Keep the Recent Activity box live while it's on screen — calls/SMS land
    // from webhooks (often seconds AFTER the call ends), so a mount-only fetch
    // goes stale mid-visit and users had to refresh the page. 15s poll pauses
    // automatically when the tab is backgrounded.
    refetchInterval: 15_000,
    refetchOnWindowFocus: "always",
  });
};

/**
 * Hook to fetch beneficiaries for a lead
 */
export function useBeneficiaries(leadId: string | undefined) {
  return useQuery<Beneficiary[], Error>({
    queryKey: ["beneficiaries", leadId],
    queryFn: async (): Promise<Beneficiary[]> => {
      if (!leadId) {
        throw new Error("Lead ID is required");
      }

      try {
        const response = await beneficiaryService.getBeneficiaries(leadId);
        
        // Check what structure we actually have
        let beneficiaries: Beneficiary[];
        if (Array.isArray(response)) {
          beneficiaries = response as Beneficiary[];
        } else if ((response as any)?.data?.data && Array.isArray((response as any).data.data)) {
          beneficiaries = (response as any).data.data as Beneficiary[];
        } else if ((response as any)?.data && Array.isArray((response as any).data)) {
          beneficiaries = (response as any).data as Beneficiary[];
        } else {
          beneficiaries = [];
        }
        
        return beneficiaries;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!leadId,
    staleTime: 0, // Always fetch fresh - set to 5 * 60 * 1000 after debugging
    retry: 1,
  });
}
/**
 * Hook to create beneficiaries
 */
export function useCreateBeneficiaries() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      beneficiaries,
    }: {
      leadId: string;
      beneficiaries: CreateBeneficiaryInput[];
    }) => {
      return await beneficiaryService.createBeneficiaries(
        leadId,
        beneficiaries
      );
    },
    onSuccess: async (data, variables) => {
      // Invalidate and refetch beneficiaries for this lead
      await queryClient.invalidateQueries({
        queryKey: ["beneficiaries", variables.leadId],
      });

      // FIX: Invalidate household queries to refresh relationship/percentage display
      await queryClient.invalidateQueries({
        queryKey: ["households"],
      });

      // Force refetch household queries to ensure immediate UI update
      await queryClient.refetchQueries({
        queryKey: ["households"],
      });
    },
  });
}

/**
 * Hook to update a beneficiary
 */
export function useUpdateBeneficiary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      beneficiaryId,
      leadId,
      data,
    }: {
      beneficiaryId: string;
      leadId: string;
      data: UpdateBeneficiaryRequest;
    }) => {
      // Pass leadId FIRST, then beneficiaryId
      return await beneficiaryService.updateBeneficiary(leadId, beneficiaryId, data);
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["beneficiaries", variables.leadId] });

      // FIX: Invalidate household queries to refresh relationship/percentage display
      await queryClient.invalidateQueries({
        queryKey: ["households"],
      });

      // Force refetch household queries to ensure immediate UI update
      await queryClient.refetchQueries({
        queryKey: ["households"],
      });
    },
  });
}
/**
 * Hook to delete a beneficiary
 */
export function useDeleteBeneficiary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      beneficiaryId,
      leadId,
    }: {
      beneficiaryId: string;
      leadId: string; // Required for endpoint
    }) => {
      // Service needs both leadId and beneficiaryId
      return await beneficiaryService.deleteBeneficiary(leadId, beneficiaryId);
    },
    onSuccess: async (data, variables) => {
      // Invalidate and refetch beneficiaries for this lead
      await queryClient.invalidateQueries({
        queryKey: ["beneficiaries", variables.leadId],
      });

      // FIX: Invalidate household queries to refresh relationship/percentage display
      await queryClient.invalidateQueries({
        queryKey: ["households"],
      });

      // Force refetch household queries to ensure immediate UI update
      await queryClient.refetchQueries({
        queryKey: ["households"],
      });
    },
  });
}

// Hook to update lead status with toast notifications
export const useUpdateLeadStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      status,
      noSaleReason,
      blockInbound,
    }: {
      leadId: string;
      status: string;
      noSaleReason?: string;
      /** Manual DNC move only — also block this lead's calls/texts to me. */
      blockInbound?: boolean;
    }) =>
      leadsService.updateLead(leadId, {
        leadStat: status,
        ...(noSaleReason ? { noSaleReason } : {}),
        ...(blockInbound ? { blockInbound: true } : {}),
      }),
    onSuccess: async (data, variables) => {
      // Invalidate AND refetch relevant queries to refresh lead data immediately
      // Using refetchQueries ensures the leads table updates instantly without page refresh
      await queryClient.invalidateQueries({ queryKey: leadsQueryKeys.lists() });
      await queryClient.refetchQueries({ queryKey: ['leads'], type: 'active' });

      queryClient.invalidateQueries({
        queryKey: leadsQueryKeys.detail(variables.leadId),
      });
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() });
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.overview() });

      showToast(
        variables.blockInbound
          ? `Moved to "${variables.status}" — their calls and texts are now blocked`
          : `Lead status updated to "${variables.status}"`,
        "success",
      );
    },
    onError: (error) => {
      showToast("Failed to update lead status. Please try again.", "error");
    },
  });
};

// ============================================================
// Lead List Names (Sources) Hook
// ============================================================

export const useListNames = () => {
  return useQuery<string[]>({
    queryKey: leadsQueryKeys.listNames(),
    queryFn: () => leadsService.getListNames(),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useTags = () => {
  return useQuery<string[]>({
    queryKey: ['leads', 'tags'],
    queryFn: () => leadsService.getTags(),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useCustomFieldKeys = () => {
  return useQuery<Array<{ key: string; type: string }>>({
    queryKey: ['leads', 'custom-field-keys'],
    queryFn: () => leadsService.getCustomFieldKeys(),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useCustomFieldValues = (key: string | null, scope?: Record<string, string | undefined>, source: 'leads' | 'assignment' = 'leads') => {
  return useQuery<string[]>({
    queryKey: ['leads', 'custom-field-values', key, scope ?? null, source],
    queryFn: () => leadsService.getCustomFieldValues(key as string, scope, source),
    enabled: !!key,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    // Same field, new filters: the old list stays up while the new one loads instead of flashing empty.
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[2] === key ? previous : undefined),
  });
};

export const useCarriers = () => {
  return useQuery<string[]>({
    queryKey: ['leads', 'carriers'],
    queryFn: () => leadsService.getCarriers(),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useCities = () => {
  return useQuery<string[]>({
    queryKey: ['leads', 'cities'],
    queryFn: () => leadsService.getCities(),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

// ============================================================
// Pipeline Stage Hooks
// ============================================================

export const usePipelineStages = () => {
  return useQuery<PipelineStage[]>({
    queryKey: leadsQueryKeys.pipelineStages(),
    queryFn: () => leadsService.getPipelineStages(),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useAllPipelineStages = () => {
  return useQuery<PipelineStage[]>({
    queryKey: leadsQueryKeys.pipelineStagesAll(),
    queryFn: () => leadsService.getAllPipelineStages(),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

// Helper to invalidate and refetch all pipeline-related queries
const invalidatePipelineQueries = async (queryClient: ReturnType<typeof useQueryClient>) => {
  // Invalidate all leads-related queries (broad prefix match)
  await queryClient.invalidateQueries({ queryKey: ["leads"] });

  // Force refetch the queries that feed the status dropdown and status cards
  // This bypasses staleTime and ensures the UI gets fresh data from the server
  await Promise.all([
    queryClient.refetchQueries({ queryKey: ["leads", "pipeline-stages"] }),
    queryClient.refetchQueries({ queryKey: ["leads", "statuses"] }),
    queryClient.refetchQueries({ queryKey: ["leads", "stats"] }),
  ]);
};

export const useCreatePipelineStage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePipelineStageParams) => leadsService.createPipelineStage(data),
    onSuccess: () => {
      invalidatePipelineQueries(queryClient);
      showToast("Pipeline stage created", "success");
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.message || "Failed to create pipeline stage";
      showToast(msg, "error");
    },
  });
};

export const useUpdatePipelineStage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePipelineStageParams }) =>
      leadsService.updatePipelineStage(id, data),
    onSuccess: () => {
      invalidatePipelineQueries(queryClient);
      showToast("Pipeline stage updated", "success");
    },
    onError: () => showToast("Failed to update pipeline stage", "error"),
  });
};

export const useTogglePipelineStageVisibility = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stageId: string) => leadsService.togglePipelineStageVisibility(stageId),
    onSuccess: () => {
      invalidatePipelineQueries(queryClient);
      showToast("Pipeline stage visibility updated", "success");
    },
    onError: () => showToast("Failed to toggle stage visibility", "error"),
  });
};

export const useDeletePipelineStage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stageId: string) => leadsService.deletePipelineStage(stageId),
    onSuccess: () => {
      invalidatePipelineQueries(queryClient);
      showToast("Pipeline stage deleted", "success");
    },
    onError: () => showToast("Failed to delete pipeline stage", "error"),
  });
};

/** Lead counts a stage delete would touch — powers the double-confirm dialog. */
export const usePipelineStageImpact = (stageId: string | null) => {
  return useQuery({
    queryKey: ["leads", "pipeline-stage-impact", stageId],
    queryFn: () => leadsService.getPipelineStageImpact(stageId as string),
    enabled: !!stageId,
    staleTime: 30_000,
  });
};

/** Soft-deleted custom stages for the Recently Deleted dialog (lazy). */
export const useDeletedPipelineStages = (opts?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["leads", "deleted-pipeline-stages"],
    queryFn: () => leadsService.getDeletedPipelineStages(),
    enabled: opts?.enabled ?? true,
  });
};

export const useRestorePipelineStage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stageId: string) => leadsService.restorePipelineStage(stageId),
    onSuccess: () => {
      invalidatePipelineQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ["leads", "deleted-pipeline-stages"] });
      showToast("Pipeline restored — its leads are back in the column", "success");
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.message || "Failed to restore pipeline stage";
      showToast(msg, "error");
    },
  });
};

export const useReorderPipelineStages = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stageIds: string[]) => leadsService.reorderPipelineStages(stageIds),
    onSuccess: () => {
      invalidatePipelineQueries(queryClient);
    },
    onError: () => showToast("Failed to reorder pipeline stages", "error"),
  });
};

export const useResetPipelineStageOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => leadsService.resetPipelineStageOrder(),
    onSuccess: () => {
      invalidatePipelineQueries(queryClient);
      showToast("Pipeline stages reset to default", "success");
    },
    onError: () => showToast("Failed to reset pipeline stages", "error"),
  });
};

export const useBulkUpdateLeadStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ leadIds, newStatus }: { leadIds: string[]; newStatus: string }) =>
      leadsService.bulkUpdateLeadStatus(leadIds, newStatus),
    onSuccess: (data, variables) => {
      // Invalidate all queries starting with "leads" — must use bare namespace since
      // all(), lists(), stats() etc. have different key structures that don't prefix-match each other
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      showToast(`${data?.updatedCount ?? variables.leadIds.length} leads moved to "${variables.newStatus}"`, "success");
    },
    onError: () => showToast("Failed to update lead status", "error"),
  });
};